import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileBarChart2, Download } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [jobs, setJobs] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [type, setType] = useState("calibration");
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);

  const fetch = async (jobId, t) => {
    setSelectedJob(jobId);
    setType(t);
    try {
      const res = await api.get(`/reports/job/${jobId}/${t}`);
      setReportData(res.data);
    } catch (e) {
      toast.error("Report unavailable");
    }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Documentation</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Reports & Compliance</h1>
        <p className="text-sm text-slate-400 mt-1.5">Generate calibration, scan, and OEM compliance reports for any job.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Select Job</div>
          <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => fetch(j.id, type)} data-testid={`report-job-${j.id}`}
                className={`w-full text-left p-3 rounded-lg border transition-colors text-sm ${
                  selectedJob === j.id ? "bg-blue-600/15 border-blue-500/40" : "bg-slate-900/40 border-slate-800 hover:border-slate-700"
                }`}>
                <div className="font-mono text-xs text-blue-300">{j.vin.slice(-8)}</div>
                <div className="text-xs mt-1">{j.vehicle_year} {j.vehicle_make}</div>
                <div className="text-[10px] text-slate-500">{j.customer_name}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="flex items-center gap-2 mb-5">
            {["calibration", "scan"].map((t) => (
              <button key={t} onClick={() => selectedJob && fetch(selectedJob, t)} data-testid={`report-type-${t}`}
                className={`px-3 py-2 text-xs uppercase tracking-wide font-bold rounded-lg border transition-colors ${
                  type === t ? "bg-blue-600/20 text-blue-300 border-blue-500/40" : "bg-slate-900/40 text-slate-400 border-slate-800"
                }`}>{t} report</button>
            ))}
            <div className="flex-1" />
            {reportData && (
              <Button variant="outline" className="border-slate-700 text-xs" size="sm" data-testid="download-report-btn" onClick={() => toast.success("Export queued (mock)")}>
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
              </Button>
            )}
          </div>

          {!reportData ? (
            <div className="text-center py-16 text-sm text-slate-500">
              <FileBarChart2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
              Select a job to generate report
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-400">{reportData.report_type}</div>
                <div className="text-xs text-slate-500 font-mono mt-1">Generated: {new Date(reportData.generated_at).toLocaleString()}</div>
              </div>
              <pre className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 text-xs overflow-x-auto font-mono text-slate-300 max-h-[600px] overflow-y-auto">
                {JSON.stringify(reportData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
